<script lang="ts">
	//UI
	import {
		HubProfileBottomLineButton,
		HubProfileTextInput,
		DefaultButton,
	} from "@hitit/new_ui";
	import ProfileViewer from "@hitit/hub/src/v7/components/profile/ProfileViewer.svelte";
	import ProfileInputFrame from "@hitit/hub/src/v7/components/profile/ProfileInputFrame.svelte";
	import Editicon from "@hitit/hub/src/assets/Icons/Edit.png";
	import CancelButton from "@hitit/hub/src/v7/components/profile/CancelButton.svelte";
	import Message from "@hitit/hub/src/v7/components/profile/Message.svelte";
	//Utils
	import { t, api } from "@hitit/hub/src/stores";
	import type { ProfileData, UserData } from "@hitit/api";
	import isEmail from "validator/lib/isEmail";
	//Types
	//Props
	export let username = "";
	export let user: UserData;
	export let profile: ProfileData;

	$: emailVerify = profile.emailVerified;
	//Constants

	//Variables
	let loading = false;
	let editmode = false;
	let sendLoading = false;
	let newEmail = "";
	let errorMessage = "";
	$: disabled = newEmail === "" || errorMessage !== "";
	$: backgroundColor = disabled ? "#a5c1f9" : "#6b93f3";
	//Functions
	function resetEmail() {
		newEmail = "";
	}

	async function sendVerifyEmail() {
		try {
			sendLoading = true;
			await $api.verifyEmailByAdmin(user.id, "ADMIN");
			profile.emailVerified = true;
		} catch (e) {
		} finally {
			sendLoading = false;
		}
	}

	async function updateEmail() {
		try {
			errorMessage = isEmail(newEmail) ? "" : "hub.profile.email_error";
			if (errorMessage) return;
			loading = true;
			await $api.updateUser(
				Object.assign({}, user, { username: newEmail }),
				user.id
			);
			profile.emailVerified = false;
			username = newEmail;
			errorMessage = "";
			editmode = false;
			loading = false;
		} catch (error) {
			errorMessage = "sso.error.unknownError";
			loading = false;
		}
	}
	//Do
	$: if (username) resetEmail();
</script>

<div class="container" class:editmode={editmode}>
	{#if editmode}
		<div class="frame">
			<div class="edit-frame" class:error={errorMessage !== ""}>
				<ProfileViewer
					value={username}
					name={$t("mbr.user.email")}
					editIcon={false}
				/>
				<ProfileInputFrame name={$t("mbr.user.new_email")}>
					<HubProfileTextInput
						invalidTextColor={Boolean(errorMessage)}
						bind:value={newEmail}
						on:input={() => (errorMessage = "")}
					/>
				</ProfileInputFrame>
				{#if errorMessage}
					<div class="error-box">
						<Message error messageTrnaslation={errorMessage} />
					</div>
				{/if}
			</div>
			<div class="button-box" class:errorMessage={errorMessage}>
				<DefaultButton
					-email-save={true}
					disabled={disabled}
					background={backgroundColor}
					fitmode
					isLoading={loading}
					on:click={updateEmail}
				>
					<span>{$t("global.save")}</span>
				</DefaultButton>
			</div>
		</div>
		<div class="cancel">
			<CancelButton on:click={() => (editmode = false)} />
		</div>
	{:else}
		<div class="frame">
			<ProfileInputFrame name={$t("hub.profile.email")}>
				<div class="email-box">
					<span class="value">{username}</span>
					{#if !profile.cellphone?.includes("Azure")}
						<img
							class="edit-icon"
							src={Editicon}
							alt="Edit Email"
							on:click={() => (editmode = true)}
						/>
					{/if}
					<div class="email-verify-box" class:verify={emailVerify}>
						<span class:verify={emailVerify} class="emailVerify">
							{$t(
								emailVerify
									? "hub.profile.verified_email"
									: "hub.profile.not_verified_email"
							)}
						</span>
						{#if !emailVerify}
							<HubProfileBottomLineButton
								loading={sendLoading}
								on:click={sendVerifyEmail}
								let:hover={emailSendHover}
							>
								<span class="email-send" class:hover={emailSendHover}>
									{$t("sso.verifyEmail.verify_email")}
								</span>
							</HubProfileBottomLineButton>
						{/if}
					</div>
				</div>
			</ProfileInputFrame>
		</div>
	{/if}
</div>

<style lang="scss">
	@import "../../../variables.scss";

	div {
		box-sizing: border-box;
	}

	.container {
		display: flex;
		position: relative;
		flex-grow: 1;
		padding: 0 20px;

		:global(.-email-save) {
			display: flex;
			justify-content: center;
			align-items: center;
		}

		.button-box {
			height: 32px;
			span {
				font-weight: 500;
				font-size: 1rem;
				color: white;
				margin-top: -1px;
			}
		}
		.frame {
			flex-grow: 1;
		}
		.email-box {
			display: flex;
			flex-direction: row;
			flex-wrap: wrap;
			align-items: center;
			gap: 10px 10px;
			flex-grow: 1;
			.edit-icon {
				width: 24px;
				height: 24.2px;
				&:hover {
					cursor: pointer;
				}
			}

			.value {
				max-width: calc(100% - 34px); /* 24 + gap */
				font-weight: 500;
				color: #46474b;
				flex-grow: 1;
				word-break: break-all;
			}

			.emailVerify {
				font-size: 0.85rem;
				color: #e54848;

				&.verify {
					color: #10c181;
				}
			}

			.email-verify-box {
				width: 100%;
				display: flex;
				justify-content: space-between;
				.email-send {
					font-weight: 200;
					font-size: 0.85rem;
					color: #3c68d6;

					&.hover {
						color: var(--default-hover-btn-1);
					}
				}
			}
		}
		&.editmode {
			background-color: #e6e7ed;
			padding: 20px;

			.frame {
				display: flex;
				flex-direction: column;
				gap: 10px;
			}
			.edit-frame {
				display: flex;
				flex-direction: column;
				gap: 20px;

				.error-box {
					margin-top: -15px;
				}
			}
			.cancel {
				position: absolute;
				top: 10px;
				right: 10px;
			}
		}
	}

	@include DesktopSize {
		.container {
			padding: 0 40px;

			&.editmode {
				padding: 20px 40px;
				flex-direction: row;
				gap: 20px;
				.frame {
					flex-direction: row;
					gap: 20px;
				}
				.edit-frame {
					flex-grow: 1;

					.error-box {
						margin-top: -10px;
					}
				}

				.cancel {
					top: 20px;
					right: 20px;
				}
				.button-box {
					width: 150px;
					height: 40px;
					margin-top: auto;

					&.errorMessage {
						margin-bottom: 26px;
					}
					span {
						font-size: 1.25rem;
					}
				}
			}

			.email-box {
				flex-direction: row;
				flex-wrap: nowrap;
				gap: 10px;
				flex-grow: 1;

				.value {
					flex-grow: 0;
					font-size: 1.25rem;
					order: 1;
				}
				.edit-icon {
					order: 3;
				}

				.email-verify-box {
					width: auto;
					order: 2;
					display: flex;
					justify-content: space-between;
					flex-grow: 1;
					font-size: 1rem;

					.email-send {
						font-size: 1rem;
					}

					&.verify {
						justify-content: flex-end;
					}

					.emailVerify {
						order: 1;
						font-size: 1rem;
					}
				}
			}
		}
	}
</style>
